export type DeepPartial<T> = { [P in keyof T]?: DeepPartial<T[P]> };
export type MapOfNumbers = { [x: string]: number };
export type MapOfBooleans = { [x: string]: boolean };
export type ErrorResponse = { error: number };
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export namespace Utils {
  export const bitmapToArray = (mask: number, sz: number): number[] => {
    let results: number[] = [];
    let arr = mask.toString(2).split("");
    arr.forEach(function(elem: string) {
      results.push(parseInt(elem));
    });
    for (let i = results.length; i < sz; i++) results.unshift(0);
    return results;
  };

  export const arrayToBitmap = (arr: number[]): number => {
    return parseInt(arr.join(""), 2);
  };

  export const diff = <T extends {}>(a: T, b: T): DeepPartial<T> => {
    let ret: DeepPartial<T> = {};
    for (let key in a) {
      let prop = a[key];
      let t = typeof prop;
      if (t === "string" || t === "number" || t === "boolean") {
        if (a[key] !== b[key]) ret = Object.assign({}, ret, { [key]: a[key] });
      } else if (t === "object") {
        ret[key] = diff(a[key], b[key]);
      }
    }
    return ret;
  };

  export const isDiff = <T extends {}>(a: T, b: T): boolean =>
    !!Object.keys(diff(a, b)).length;

  export const convertBooleansToNumbers = <
    T extends {},
    K extends keyof T & keyof MapOfNumbers
  >(
    data: Partial<T>,
    keys: K[]
  ): MapOfNumbers => {
    let ret: MapOfNumbers = {};
    keys.forEach(key => {
      let prop: any | undefined = data[key];
      if (typeof prop == "boolean") ret[key] = Utils.boolToNum(prop);
    });
    return ret;
  };

  export const convertNumbersToBooleans = <
    T extends {},
    K extends keyof T & keyof MapOfBooleans
  >(
    data: Partial<T>,
    keys: K[]
  ): MapOfBooleans => {
    let ret: MapOfBooleans = {};
    keys.forEach(key => {
      let prop: any | undefined = data[key];
      if (typeof prop == "number") ret[key] = Utils.numToBool(prop);
    });
    return ret;
  };

  export const convertUxToServer = <
    T extends {},
    K extends keyof T & keyof MapOfNumbers
  >(
    data: Partial<T>,
    keys: K[]
  ): MapOfNumbers => {
    let ret: MapOfNumbers = {};
    keys.forEach(key => {
      let prop: any | undefined = data[key];
      if (typeof prop == "number") ret[key] = Utils.scaleServer(prop);
    });
    return ret;
  };

  export const convertServerToUx = <
    T extends {},
    K extends keyof T & keyof MapOfNumbers
  >(
    data: Partial<T>,
    keys: K[]
  ): MapOfNumbers => {
    let ret: MapOfNumbers = {};
    keys.forEach(key => {
      let prop: any | undefined = data[key];
      if (typeof prop == "number") ret[key] = Utils.scaleUx(prop);
    });
    return ret;
  };

  export const numToBool = (data: number): boolean =>
    data == 1 ? true : false;

  export const boolToNum = (data: boolean): number => (data == true ? 1 : 0);

  export const scaleUx = (data: number, precision: number = 2) =>
    parseFloat((data / 1000).toFixed(precision));

  export const scaleServer = (data: number) => data * 1000;

  export function selectMap<T>(arg: T, map: T[]): number;
  export function selectMap<T>(arg: number, map: T[]): T;
  export function selectMap<T>(arg: number | T, map: T[]) {
    return typeof arg == "number" ? map[arg] : map.indexOf(arg);
  }
}

export default Utils;
